import { useCallback, useEffect, useState } from "react";
import { CheckCircle2, Loader2, Plus, Truck, XCircle } from "lucide-react";
import { api, apiError, inr, shortDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Empty } from "@/components/Layout";
import { Field } from "@/pages/Leads";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const nextUid = () => `line-${Math.random().toString(36).slice(2, 10)}`;

export default function Purchase() {
  const { can } = useAuth();
  const [prs, setPrs] = useState([]);
  const [pos, setPos] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [stock, setStock] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [prOpen, setPrOpen] = useState(false);
  const [poOpen, setPoOpen] = useState(false);
  const [receiving, setReceiving] = useState(null);
  const [receiveLines, setReceiveLines] = useState({});
  const [pr, setPr] = useState({ items: [{ uid: nextUid(), name: "", quantity: 1, unit: "Nos", estimated_rate: 0 }], reason: "", priority: "Medium" });
  const [po, setPo] = useState({ supplier_id: "", warehouse_id: "", purchase_request_id: "", items: [{ uid: nextUid(), name: "", quantity: 1, unit: "Nos", rate: 0, gst_rate: 18 }], expected_date: "", notes: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, b, c, d, e] = await Promise.all([
        api.get("/purchase-requests"),
        api.get("/purchase-orders"),
        api.get("/suppliers"),
        api.get("/stock"),
        api.get("/warehouses"),
      ]);
      setPrs(a.data);
      setPos(b.data);
      setSuppliers(c.data);
      setStock(d.data.items);
      setWarehouses(e.data);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const decide = async (id, decision) => {
    try {
      await api.post(`/purchase-requests/${id}/decision`, { decision });
      toast.success(`Purchase request ${decision === "approve" ? "approved" : "rejected"}`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const savePr = async () => {
    try {
      const items = pr.items
        .filter((i) => i.name.trim())
        .map(({ uid, ...i }) => ({ ...i, quantity: Number(i.quantity || 0), estimated_rate: Number(i.estimated_rate || 0) }));
      if (!items.length) return toast.error("Add at least one item");
      await api.post("/purchase-requests", { ...pr, items });
      toast.success("Purchase request submitted for approval");
      setPrOpen(false);
      setPr({ items: [{ uid: nextUid(), name: "", quantity: 1, unit: "Nos", estimated_rate: 0 }], reason: "", priority: "Medium" });
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const savePo = async () => {
    try {
      if (!po.supplier_id) return toast.error("Select a supplier");
      const items = po.items
        .filter((i) => i.name.trim())
        .map(({ uid, ...i }) => ({ ...i, quantity: Number(i.quantity || 0), rate: Number(i.rate || 0), gst_rate: Number(i.gst_rate || 0) }));
      if (!items.length) return toast.error("Add at least one item");
      const wh = warehouses.find((w) => w.id === po.warehouse_id);
      await api.post("/purchase-orders", { ...po, items, warehouse_name: wh?.name || "" });
      toast.success("Purchase order created");
      setPoOpen(false);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const submitReceive = async () => {
    try {
      const lines = Object.entries(receiveLines)
        .map(([index, quantity]) => ({ index: Number(index), quantity: Number(quantity) }))
        .filter((l) => l.quantity > 0);
      if (!lines.length) return toast.error("Enter received quantities");
      await api.post(`/purchase-orders/${receiving.id}/receive`, { lines });
      toast.success("Goods received and stock updated");
      setReceiving(null);
      setReceiveLines({});
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const fromPr = (p) => {
    setPo({
      supplier_id: "", warehouse_id: warehouses[0]?.id || "", purchase_request_id: p.id,
      items: (p.items || []).map((i) => ({
        uid: nextUid(),
        stock_item_id: i.stock_item_id || "", name: i.name, quantity: i.quantity,
        unit: i.unit, rate: i.estimated_rate || 0, gst_rate: 18,
      })),
      expected_date: "", notes: `Against ${p.number}`,
    });
    setPoOpen(true);
  };

  return (
    <div>
      <PageHeader
        testId="purchase-header"
        title="Purchase"
        subtitle="Request → approval → purchase order → goods receipt → stock."
        actions={
          <>
            {can("purchase", "create") && (
              <Dialog open={prOpen} onOpenChange={setPrOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" data-testid="new-pr-button" className="gap-1.5">
                    <Plus className="h-4 w-4" /> Purchase Request
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-xl">
                  <DialogHeader>
                    <DialogTitle>New purchase request</DialogTitle>
                  </DialogHeader>
                  {pr.items.map((it, i) => (
                    <div key={it.uid} className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                      <Field label="Item" className="col-span-2">
                        <Select
                          value={it.stock_item_id || ""}
                          onValueChange={(v) => {
                            const s = stock.find((x) => x.id === v);
                            const items = [...pr.items];
                            items[i] = { ...items[i], stock_item_id: v, name: s?.name || "", unit: s?.unit || "Nos", estimated_rate: s?.rate || 0 };
                            setPr({ ...pr, items });
                          }}
                        >
                          <SelectTrigger data-testid={`pr-item-select-${i}`}>
                            <SelectValue placeholder="Select stock item" />
                          </SelectTrigger>
                          <SelectContent>
                            {stock.map((s) => (
                              <SelectItem key={s.id} value={s.id}>
                                {s.name} ({s.quantity} {s.unit})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </Field>
                      <Field label="Qty">
                        <Input
                          data-testid={`pr-qty-input-${i}`}
                          value={it.quantity}
                          onChange={(e) => {
                            const items = [...pr.items];
                            items[i] = { ...items[i], quantity: e.target.value };
                            setPr({ ...pr, items });
                          }}
                        />
                      </Field>
                      <Field label="Est. rate ₹">
                        <Input
                          value={it.estimated_rate}
                          onChange={(e) => {
                            const items = [...pr.items];
                            items[i] = { ...items[i], estimated_rate: e.target.value };
                            setPr({ ...pr, items });
                          }}
                        />
                      </Field>
                    </div>
                  ))}
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid="pr-add-line-button"
                    onClick={() => setPr({ ...pr, items: [...pr.items, { uid: nextUid(), name: "", quantity: 1, unit: "Nos", estimated_rate: 0 }] })}
                  >
                    Add line
                  </Button>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <Field label="Priority">
                      <Select value={pr.priority} onValueChange={(v) => setPr({ ...pr, priority: v })}>
                        <SelectTrigger data-testid="pr-priority-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {["Low", "Medium", "High", "Urgent"].map((p) => (
                            <SelectItem key={p} value={p}>
                              {p}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                    <Field label="Reason">
                      <Input
                        data-testid="pr-reason-input"
                        value={pr.reason}
                        onChange={(e) => setPr({ ...pr, reason: e.target.value })}
                      />
                    </Field>
                  </div>
                  <DialogFooter>
                    <Button data-testid="pr-save-button" onClick={savePr}>
                      Submit request
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
            {can("purchase", "create") && (
              <Dialog open={poOpen} onOpenChange={setPoOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="new-po-button" className="gap-1.5">
                    <Plus className="h-4 w-4" /> Purchase Order
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>New purchase order</DialogTitle>
                  </DialogHeader>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <Field label="Supplier">
                      <Select value={po.supplier_id} onValueChange={(v) => setPo({ ...po, supplier_id: v })}>
                        <SelectTrigger data-testid="po-supplier-select">
                          <SelectValue placeholder="Select supplier" />
                        </SelectTrigger>
                        <SelectContent>
                          {suppliers.map((s) => (
                            <SelectItem key={s.id} value={s.id}>
                              {s.company}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                    <Field label="Receive at warehouse">
                      <Select value={po.warehouse_id} onValueChange={(v) => setPo({ ...po, warehouse_id: v })}>
                        <SelectTrigger data-testid="po-warehouse-select">
                          <SelectValue placeholder="Select warehouse" />
                        </SelectTrigger>
                        <SelectContent>
                          {warehouses.map((w) => (
                            <SelectItem key={w.id} value={w.id}>
                              {w.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                  </div>
                  {po.items.map((it, i) => (
                    <div key={it.uid} className="grid grid-cols-2 gap-2 sm:grid-cols-5">
                      <Field label="Item" className="col-span-2">
                        <Select
                          value={it.stock_item_id || ""}
                          onValueChange={(v) => {
                            const s = stock.find((x) => x.id === v);
                            const items = [...po.items];
                            items[i] = { ...items[i], stock_item_id: v, name: s?.name || "", unit: s?.unit || "Nos", rate: s?.rate || 0 };
                            setPo({ ...po, items });
                          }}
                        >
                          <SelectTrigger data-testid={`po-item-select-${i}`}>
                            <SelectValue placeholder="Select item" />
                          </SelectTrigger>
                          <SelectContent>
                            {stock.map((s) => (
                              <SelectItem key={s.id} value={s.id}>
                                {s.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </Field>
                      {[["quantity", "Qty"], ["rate", "Rate ₹"], ["gst_rate", "GST %"]].map(([k, l]) => (
                        <Field key={k} label={l}>
                          <Input
                            data-testid={`po-${k}-input-${i}`}
                            value={it[k]}
                            onChange={(e) => {
                              const items = [...po.items];
                              items[i] = { ...items[i], [k]: e.target.value };
                              setPo({ ...po, items });
                            }}
                          />
                        </Field>
                      ))}
                    </div>
                  ))}
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid="po-add-line-button"
                    onClick={() => setPo({ ...po, items: [...po.items, { uid: nextUid(), name: "", quantity: 1, unit: "Nos", rate: 0, gst_rate: 18 }] })}
                  >
                    Add line
                  </Button>
                  <Field label="Notes">
                    <Textarea value={po.notes} onChange={(e) => setPo({ ...po, notes: e.target.value })} />
                  </Field>
                  <DialogFooter>
                    <Button data-testid="po-save-button" onClick={savePo}>
                      Create order
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </>
        }
      />

      {loading ? (
        <div className="flex h-40 items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <Tabs defaultValue="requests" className="px-4 pt-4 sm:px-6">
          <TabsList data-testid="purchase-tabs">
            <TabsTrigger value="requests">Requests ({prs.length})</TabsTrigger>
            <TabsTrigger value="orders">Orders ({pos.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="requests">
            {prs.length === 0 ? (
              <Empty title="No purchase requests" hint="Requests can be raised manually or created from AI employee reports." />
            ) : (
              <div className="surface overflow-x-auto" data-testid="pr-table">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Number</TableHead>
                      <TableHead>Items</TableHead>
                      <TableHead>Priority</TableHead>
                      <TableHead>Source</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Value</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {prs.map((p) => (
                      <TableRow key={p.id} data-testid={`pr-row-${p.id}`}>
                        <TableCell className="font-mono-num text-xs">{p.number}</TableCell>
                        <TableCell className="text-sm">
                          {(p.items || []).map((i) => `${i.name} × ${i.quantity} ${i.unit}`).join(", ")}
                          <div className="text-xs text-muted-foreground">{p.reason}</div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={p.priority === "Urgent" ? "destructive" : "outline"} className="text-[10px]">
                            {p.priority}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={p.source === "ai" ? "default" : "secondary"} className="text-[10px]">
                            {p.source === "ai" ? "AI" : "Manual"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs">{p.status}</TableCell>
                        <TableCell className="text-right font-mono-num text-sm">{inr(p.estimated_value)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            {p.status === "Pending Approval" && can("purchase", "approve") && (
                              <>
                                <Button
                                  size="sm"
                                  data-testid={`pr-approve-${p.id}`}
                                  onClick={() => decide(p.id, "approve")}
                                >
                                  <CheckCircle2 className="h-3.5 w-3.5" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  data-testid={`pr-reject-${p.id}`}
                                  onClick={() => decide(p.id, "reject")}
                                >
                                  <XCircle className="h-3.5 w-3.5" />
                                </Button>
                              </>
                            )}
                            {p.status === "Approved" && can("purchase", "create") && (
                              <Button size="sm" variant="outline" data-testid={`pr-to-po-${p.id}`} onClick={() => fromPr(p)}>
                                Create PO
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </TabsContent>

          <TabsContent value="orders">
            {pos.length === 0 ? (
              <Empty title="No purchase orders" />
            ) : (
              <div className="surface overflow-x-auto" data-testid="po-table">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Number</TableHead>
                      <TableHead>Supplier</TableHead>
                      <TableHead>Items</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Value</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pos.map((p) => (
                      <TableRow key={p.id} data-testid={`po-row-${p.id}`}>
                        <TableCell className="font-mono-num text-xs">
                          {p.number}
                          <div className="text-[10px] text-muted-foreground">{shortDate(p.created_at)}</div>
                        </TableCell>
                        <TableCell className="text-sm">{p.supplier_name}</TableCell>
                        <TableCell className="text-xs">
                          {(p.items || [])
                            .map((i) => `${i.name} ${i.received_quantity || 0}/${i.quantity} ${i.unit}`)
                            .join(", ")}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-[10px]">
                            {p.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-mono-num text-sm">{inr(p.grand_total)}</TableCell>
                        <TableCell className="text-right">
                          {p.status !== "Received" && can("purchase", "edit") && (
                            <Button
                              size="sm"
                              variant="outline"
                              data-testid={`po-receive-${p.id}`}
                              onClick={() => {
                                setReceiving(p);
                                setReceiveLines({});
                              }}
                              className="gap-1"
                            >
                              <Truck className="h-3.5 w-3.5" /> Receive
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </TabsContent>
        </Tabs>
      )}

      <Dialog open={!!receiving} onOpenChange={(o) => !o && setReceiving(null)}>
        <DialogContent data-testid="receive-dialog">
          <DialogHeader>
            <DialogTitle>Receive goods — {receiving?.number}</DialogTitle>
          </DialogHeader>
          {(receiving?.items || []).map((it, i) => (
            <Field key={`${it.name}-${i}`} label={`${it.name} (ordered ${it.quantity} ${it.unit}, received ${it.received_quantity || 0})`}>
              <Input
                data-testid={`receive-qty-${i}`}
                type="number"
                placeholder="Quantity received now"
                value={receiveLines[i] ?? ""}
                onChange={(e) => setReceiveLines({ ...receiveLines, [i]: e.target.value })}
              />
            </Field>
          ))}
          <DialogFooter>
            <Button data-testid="receive-submit-button" onClick={submitReceive}>
              Post receipt
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
